import { useState, useEffect, useCallback } from 'react';

export type ThemeMode = 'light' | 'dark' | 'high-contrast' | 'calm' | 'warm';

export interface ThemeConfig {
  mode: ThemeMode;
  reducedMotion: boolean;
  fontSize: 'small' | 'medium' | 'large';
  colorIntensity: 'low' | 'medium' | 'high';
}

const defaultTheme: ThemeConfig = {
  mode: 'light',
  reducedMotion: false,
  fontSize: 'medium',
  colorIntensity: 'medium'
};

export function useTheme() {
  const [theme, setTheme] = useState<ThemeConfig>(() => {
    // Check localStorage first, then system preferences
    const saved = localStorage.getItem('neurocademy-theme');
    if (saved) {
      try {
        return { ...defaultTheme, ...JSON.parse(saved) };
      } catch {
        return defaultTheme;
      }
    }
    
    // Check system preferences
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    
    return {
      ...defaultTheme,
      mode: prefersDark ? 'dark' : 'light',
      reducedMotion: prefersReducedMotion
    };
  });

  const applyThemeVariables = useCallback((themeConfig: ThemeConfig) => {
    const root = document.documentElement;
    
    // Enhanced color palettes with improved contrast for all themes
    const colorPalettes = {
      light: {
        // Enhanced light theme with better contrast
        '--bg-primary': '#ffffff',
        '--bg-secondary': '#f8fafc',
        '--bg-tertiary': '#f1f5f9',
        '--text-primary': '#0f172a', // Darker for better contrast
        '--text-secondary': '#334155', // Darker for better readability
        '--text-muted': '#64748b',
        '--border-color': '#e2e8f0',
        '--accent-blue': '#2563eb', // Slightly darker for better contrast
        '--accent-green': '#059669', // Darker for better contrast
        '--accent-purple': '#7c3aed', // Darker for better contrast
        '--accent-orange': '#ea580c', // Darker for better contrast
        '--accent-red': '#dc2626', // Darker for better contrast
        '--accent-yellow': '#ca8a04', // Darker for better contrast
        '--accent-cyan': '#0891b2', // Darker for better contrast
        '--accent-teal': '#0d9488', // Darker for better contrast
        '--shadow': '0 1px 3px 0 rgb(0 0 0 / 0.1)',
      },
      dark: {
        // Enhanced dark theme with better contrast
        '--bg-primary': '#0a0f1c', // Darker for better contrast
        '--bg-secondary': '#1a2332', // Slightly lighter than primary
        '--bg-tertiary': '#2a3441', // Lighter for layering
        '--text-primary': '#ffffff', // Pure white for maximum contrast
        '--text-secondary': '#e2e8f0', // Lighter for better readability
        '--text-muted': '#94a3b8',
        '--border-color': '#3f4956', // Lighter for visibility
        '--accent-blue': '#60a5fa', // Bright for visibility
        '--accent-green': '#34d399', // Bright for visibility
        '--accent-purple': '#a78bfa', // Bright for visibility
        '--accent-orange': '#fbbf24', // Bright for visibility
        '--accent-red': '#f87171', // Bright for visibility
        '--accent-yellow': '#fde047', // Bright for visibility
        '--accent-cyan': '#22d3ee', // Bright for visibility
        '--accent-teal': '#2dd4bf', // Bright for visibility
        '--shadow': '0 4px 6px 0 rgb(0 0 0 / 0.5)', // Stronger shadow for depth
      },
      'high-contrast': {
        '--bg-primary': '#000000',
        '--bg-secondary': '#1a1a1a',
        '--bg-tertiary': '#333333',
        '--text-primary': '#ffffff',
        '--text-secondary': '#e0e0e0',
        '--text-muted': '#cccccc',
        '--border-color': '#666666',
        '--accent-blue': '#00bfff',
        '--accent-green': '#00ff00',
        '--accent-purple': '#ff00ff',
        '--accent-orange': '#ffa500',
        '--accent-red': '#ff0000',
        '--accent-yellow': '#ffff00',
        '--accent-cyan': '#00ffff',
        '--accent-teal': '#00ffbf',
        '--shadow': '0 2px 4px 0 rgb(255 255 255 / 0.1)',
      },
      calm: {
        '--bg-primary': '#f0f9ff',
        '--bg-secondary': '#e0f2fe',
        '--bg-tertiary': '#bae6fd',
        '--text-primary': '#0c4a6e',
        '--text-secondary': '#0369a1',
        '--text-muted': '#0284c7',
        '--border-color': '#7dd3fc',
        '--accent-blue': '#0ea5e9',
        '--accent-green': '#06b6d4',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#0891b2',
        '--accent-teal': '#0d9488',
        '--shadow': '0 1px 3px 0 rgb(14 165 233 / 0.1)',
      },
      warm: {
        '--bg-primary': '#fefcf3',
        '--bg-secondary': '#fef3c7',
        '--bg-tertiary': '#fde68a',
        '--text-primary': '#78350f',
        '--text-secondary': '#92400e',
        '--text-muted': '#a16207',
        '--border-color': '#fbbf24',
        '--accent-blue': '#3b82f6',
        '--accent-green': '#10b981',
        '--accent-purple': '#8b5cf6',
        '--accent-orange': '#f59e0b',
        '--accent-red': '#ef4444',
        '--accent-yellow': '#eab308',
        '--accent-cyan': '#06b6d4',
        '--accent-teal': '#14b8a6',
        '--shadow': '0 1px 3px 0 rgb(245 158 11 / 0.1)',
      }
    };
    
    // Apply base colors
    const palette = colorPalettes[themeConfig.mode as keyof typeof colorPalettes];
    Object.entries(palette).forEach(([property, value]) => {
      root.style.setProperty(property, value);
    });
    
    // Apply font size scaling
    const fontSizes = {
      small: '0.875',
      medium: '1',
      large: '1.125'
    };
    root.style.setProperty('--font-scale', fontSizes[themeConfig.fontSize as keyof typeof fontSizes]);
    
    // Apply color intensity
    const intensities = {
      low: '0.7',
      medium: '1',
      high: '1.3'
    };
    root.style.setProperty('--color-intensity', intensities[themeConfig.colorIntensity as keyof typeof intensities]);
  }, []);

  const applyThemeToDocument = useCallback((themeConfig: ThemeConfig) => {
    const root = document.documentElement;

    // Remove all theme classes
    root.classList.remove('theme-light', 'theme-dark', 'theme-high-contrast', 'theme-calm', 'theme-warm');
    root.classList.remove('reduced-motion', 'font-small', 'font-medium', 'font-large');
    root.classList.remove('color-low', 'color-medium', 'color-high');

    // Apply current theme
    root.classList.add(`theme-${themeConfig.mode}`);

    if (themeConfig.reducedMotion) {
      root.classList.add('reduced-motion');
    }

    root.classList.add(`font-${themeConfig.fontSize}`);
    root.classList.add(`color-${themeConfig.colorIntensity}`);

    // Set CSS custom properties for dynamic theming
    applyThemeVariables(themeConfig);
  }, [applyThemeVariables]);

  useEffect(() => {
    // Apply theme to document immediately
    applyThemeToDocument(theme);

    // Save to localStorage
    localStorage.setItem('neurocademy-theme', JSON.stringify(theme));

    // Dispatch custom event for components that need to react to theme changes
    window.dispatchEvent(new CustomEvent('themeChange', { detail: theme }));
  }, [theme, applyThemeToDocument]);

  const updateTheme = (updates: Partial<ThemeConfig>) => {
    const newTheme = { ...theme, ...updates };
    setTheme(newTheme);
  };

  const toggleTheme = () => {
    const modes: ThemeMode[] = ['light', 'dark', 'high-contrast', 'calm', 'warm'];
    const currentIndex = modes.indexOf(theme.mode);
    const nextIndex = (currentIndex + 1) % modes.length;
    updateTheme({ mode: modes[nextIndex] });
  };

  // Simple toggle between light and dark only (for parent/teacher portals)
  const toggleDarkMode = () => {
    updateTheme({ mode: theme.mode === 'dark' ? 'light' : 'dark' });
  };

  const isDarkMode = theme.mode === 'dark' || theme.mode === 'high-contrast';

  return {
    theme,
    updateTheme,
    toggleTheme,
    toggleDarkMode,
    isDarkMode,
    isHighContrast: theme.mode === 'high-contrast',
    isCalm: theme.mode === 'calm',
    isWarm: theme.mode === 'warm',
  };
}